import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import { StoresService, type IGetStoresParams } from '@services/stores.service';

// --- Stores ---
import { StoresStore } from '@stores/stores.store';

// --- Interfaces ---
import type { IStore } from '@interfaces/store';

const REFRESH_INTERVAL = 120 * 1000;

@Injectable({
	providedIn: 'root',
})
export class StoresFacade {
	currentStore: IStore | null = null;

	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);

	private readonly _filter = new BehaviorSubject<IGetStoresParams>({});
	readonly filter$ = this._filter.asObservable().pipe(distinctUntilChanged());

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this._refresh.asObservable(),
	]).pipe(switchMap(() => this.getStores({ ...this._filter.value })));

	readonly storesState$ = combineLatest([
		this.storesStore.storesState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => {
			return state;
		}),
		shareReplay({ refCount: true })
	);

	constructor(
		private storesService: StoresService,
		private storesStore: StoresStore
	) {}

	getStores(queryParams?: IGetStoresParams) {
		return this.storesService
			.getStores(queryParams)
			.pipe(tap(stores => this.storesStore.updateStores(stores)));
	}

	getStoreById(id: IStore['id']) {
		return this.storesService.getStoreById(id);
	}

	newStore(store: IStore) {
		return this.storesService
			.postStore(store)
			.pipe(tap(store => this.storesStore.updateStore(store)));
	}

	updateStore(store: IStore) {
		return this.storesService
			.updateStore(store)
			.pipe(tap(() => this.storesStore.updateStore(store)));
	}

	deleteStore(store: IStore) {
		return this.storesService
			.deleteStore(store.id)
			.pipe(tap(() => this.storesStore.deleteStore(store)));
	}

	activateStore(store: IStore) {
		return this.storesService
			.activate(store.id)
			.pipe(tap(() => this.storesStore.deleteStore(store)));
	}

	filterStores(filter: IGetStoresParams) {
		this._filter.next({ ...this._filter.value, ...filter });
	}
}
